import { useEffect, type ReactNode } from "react";

import { useCombobox } from "./internal/ComboboxContext";

/**
 * Props for the ComboboxEmpty component
 */
export type ComboboxEmptyProps = {
  children?: ReactNode;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ComboboxEmptyElement = function <Item = any>({
  children,
}: ComboboxEmptyProps) {
  const { items, setHasEmpty } = useCombobox<Item>();

  useEffect(() => {
    setHasEmpty?.(true);

    return () => {
      setHasEmpty?.(false);
    };
  });

  return !items.length ? children : null;
};

/**
 * ComboboxEmpty component for rendering content when no items are available
 *
 * Features:
 * - Only renders children when the combobox has no items
 * - Integrates with combobox state management
 * - Automatically manages empty state visibility
 *
 * @template Item - The type of items in the combobox
 * @param props - The component props
 * @returns The children when no items are available, or null
 *
 * @example
 * <ComboboxEmpty>
 *   <Text>No items found. Try a different search term.</Text>
 * </ComboboxEmpty>
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ComboboxEmpty = function <Item = any>({
  children,
}: ComboboxEmptyProps) {
  return <ComboboxEmptyElement<Item>>{children}</ComboboxEmptyElement>;
};
